import { z } from "zod";

const PAGE_SIZE = 10;

// Keep the members in sync with `AssetSortBy` in ../../domain/models -- the
// columns the real GET /assets can sort by.
export const assetSortBySchema = z.enum([
  "code",
  "nameEn",
  "nameAr",
  "accountNameEn",
  "accountNameAr",
  "assetTypeNameEn",
  "assetTypeNameAr",
  "status",
  "createdAt",
]);

export const assetsSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
  sort: z.enum(["asc", "desc"]).optional(),
  sortBy: assetSortBySchema.optional(),
  // No `.trim()` here -- this value round-trips through the URL on every
  // keystroke (the search input is fully controlled by router state), so
  // trimming on parse would strip a trailing space the instant it's typed,
  // making it impossible to type one at all. The actual request still trims
  // (see AssetsListScreen's own toQuery).
  search: z.string().min(1).optional(),
  status: z.enum(["ACTIVE", "INACTIVE"]).optional(),
});

export type AssetsSearch = z.infer<typeof assetsSearchSchema>;

// GET /assets/archived only sorts by archive date (most recent first by
// default) -- no sortBy param, same convention as archivedAssetTypesSearchSchema.
export const archivedAssetsSearchSchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
    sort: z.enum(["asc", "desc"]).optional(),
    search: z.string().trim().min(1).optional(),
    status: z.enum(["ACTIVE", "INACTIVE"]).optional(),
    fromArchivedAt: z.string().datetime().optional(),
    toArchivedAt: z.string().datetime().optional(),
  })
  .refine(
    (search) =>
      (search.fromArchivedAt === undefined) ===
      (search.toArchivedAt === undefined),
  );

export type ArchivedAssetsSearch = z.infer<typeof archivedAssetsSearchSchema>;
